// src/pages/Login.jsx
import React, { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import LocalMallRoundedIcon from "@mui/icons-material/LocalMallRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import PrivacyTipRoundedIcon from "@mui/icons-material/PrivacyTipRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";

import { useAuth } from "../contexts/AuthContext";
import sintanetLogo from "../assets/sintanet-logo.png";
import brunoImg from "../assets/bruno.png";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
  return emailRegex.test(String(value).trim().toLowerCase());
}

function LegalButton({ to, icon, children }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      startIcon={icon}
      size="small"
      sx={{
        borderRadius: "999px",
        px: 1.45,
        py: 0.72,
        textTransform: "none",
        fontWeight: 850,
        color: "#667085",
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        boxShadow: "0 8px 20px rgba(15,23,42,0.035)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Button>
  );
}

function InfoCard({ icon, title, text, color }) {
  return (
    <Box
      sx={{
        width: 220,
        height: 210,
        p: 2.7,
        borderRadius: "24px",
        bgcolor: "rgba(255,255,255,0.82)",
        border: "1px solid rgba(226,232,240,0.95)",
        boxShadow: "0 18px 45px rgba(15,23,42,0.07)",
        backdropFilter: "blur(14px)",
      }}
    >
      <Avatar
        sx={{
          width: 58,
          height: 58,
          borderRadius: "22px",
          bgcolor: color.bg,
          color: color.main,
          mb: 2,
        }}
      >
        {icon}
      </Avatar>

      <Typography fontWeight={950} color="#111827">
        {title}
      </Typography>

      <Typography
        color="#667085"
        fontSize=".92rem"
        sx={{ mt: 0.8, lineHeight: 1.55 }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function ContactItem({ icon, title, subtitle }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: "#eef2ff",
          color: "#4f46e5",
        }}
      >
        {icon}
      </Avatar>

      <Box>
        <Typography fontSize=".76rem" fontWeight={950} color="#334155">
          {title}
        </Typography>
        <Typography fontSize=".7rem" color="#64748b">
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formValid = useMemo(
    () => isValidEmail(email) && password.length >= 6,
    [email, password]
  );

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      height: 54,
      borderRadius: "18px",
      bgcolor: "#fff",
      boxShadow: "0 16px 38px rgba(15,23,42,0.06)",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#bfdbfe" },
      "&.Mui-focused fieldset": { borderColor: "#60a5fa" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#4f46e5" },
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setError("");

    const cleanEmail = email.trim();

    if (!isValidEmail(cleanEmail)) {
      setError("Insere um e-mail válido.");
      return;
    }

    if (password.length < 6) {
      setError("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }

    try {
      setLoading(true);
      await login(cleanEmail, password, { remember });
      navigate("/");
    } catch (err) {
      setError(err?.message || "Não foi possível iniciar sessão.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, md: "16px" },
        bgcolor: "#f8fbff",
        overflowX: "hidden",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: "100%", md: 1450 },
          maxWidth: { xs: "calc(100vw - 24px)", md: "calc(100vw - 32px)" },
          minHeight: { xs: "auto", md: 900 },
          maxHeight: { xs: "none", md: "calc(100vh - 32px)" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "520px 1fr" },
          overflow: "hidden",
          borderRadius: { xs: "28px", md: "34px" },
          bgcolor: "#fff",
          border: "1px solid rgba(226,232,240,0.9)",
          boxShadow: "0 30px 90px rgba(15,23,42,0.12)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: "#fbfdff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 4, md: 0 },
          }}
        >
          <Chip
            icon={<BoltRoundedIcon />}
            label="Sistema online"
            sx={{
              display: { xs: "none", md: "inline-flex" },
              position: "absolute",
              top: 34,
              right: 54,
              height: 34,
              borderRadius: "999px",
              bgcolor: "#ecfeff",
              color: "#06b6d4",
              border: "1px solid #bae6fd",
              fontWeight: 950,
              "& .MuiChip-icon": { color: "#06b6d4" },
            }}
          />

          <Box sx={{ width: "100%", maxWidth: 430 }}>
            <Stack
              spacing={0.6}
              sx={{
                mb: { xs: 3, md: 5 },
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 32, sm: 38, md: 45 },
                  fontWeight: 950,
                  letterSpacing: "-0.06em",
                  color: "#111827",
                  lineHeight: 1,
                }}
              >
                Bem vindo(a)!
              </Typography>

              <Typography color="#667085">
                Inicia sessão para continuar.
              </Typography>
            </Stack>

            {error && (
              <Alert
                severity="error"
                onClose={() => setError("")}
                sx={{ mb: 2, borderRadius: "18px" }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={1.6}>
                <TextField
                  label="E-mail"
                  type="email"
                  required
                  fullWidth
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  error={email.length > 0 && !isValidEmail(email)}
                  helperText={
                    email.length > 0 && !isValidEmail(email)
                      ? "E-mail inválido"
                      : " "
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailRoundedIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyles}
                />

                <TextField
                  label="Palavra-passe"
                  type={showPassword ? "text" : "password"}
                  required
                  fullWidth
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  error={password.length > 0 && password.length < 6}
                  helperText={
                    password.length > 0 && password.length < 6
                      ? "Mínimo 6 caracteres"
                      : " "
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockRoundedIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? (
                            <VisibilityOffRoundedIcon />
                          ) : (
                            <VisibilityRoundedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyles}
                />

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Lembrar-me</Typography>}
                    sx={{ ml: -0.5 }}
                  />

                  <RouterLink
                    to="/forgot-password"
                    style={{ textDecoration: "none" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#4f46e5", fontWeight: 950 }}
                    >
                      Esqueceste a password?
                    </Typography>
                  </RouterLink>
                </Stack>

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={loading || !formValid}
                  endIcon={!loading ? <ArrowForwardRoundedIcon /> : null}
                  sx={{
                    mt: 0.8,
                    py: 1.65,
                    borderRadius: "18px",
                    textTransform: "none",
                    color: "#fff",
                    fontSize: "1rem",
                    fontWeight: 950,
                    background: "linear-gradient(90deg, #6366f1, #60a5fa)",
                    boxShadow: "0 18px 42px rgba(96,165,250,0.34)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={23} color="inherit" />
                  ) : (
                    "Entrar na plataforma"
                  )}
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                mt: 2.4,
                p: 2.1,
                borderRadius: "20px",
                border: "1px solid #bae6fd",
                bgcolor: "#f0fdff",
                textAlign: "center",
              }}
            >
              <Typography color="#667085">
                Ainda não tens conta cliente?{" "}
                <RouterLink to="/register" style={{ textDecoration: "none" }}>
                  <Typography component="span" fontWeight={950} color="#4f46e5">
                    Criar conta
                  </Typography>
                </RouterLink>
              </Typography>
            </Box>

            <Divider sx={{ my: 3.2 }} />

            <Stack alignItems="center" spacing={1.6}>
              <Typography
                variant="caption"
                sx={{
                  color: "#667085",
                  fontWeight: 950,
                  letterSpacing: ".04em",
                  textAlign: "center",
                }}
              >
                INFORMAÇÃO LEGAL E APOIO
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
              >
                <LegalButton to="/termos-condicoes" icon={<GavelRoundedIcon />}>
                  Termos
                </LegalButton>

                <LegalButton
                  to="/politica-privacidade"
                  icon={<PrivacyTipRoundedIcon />}
                >
                  Privacidade
                </LegalButton>

                <LegalButton
                  to="/livro-reclamacoes"
                  icon={<ReportProblemRoundedIcon />}
                >
                  Reclamações
                </LegalButton>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "relative",
            overflow: "hidden",
            bgcolor: "#f8fbff",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -95,
              right: -20,
              width: 430,
              height: 430,
              borderRadius: "36%",
              bgcolor: "rgba(99,102,241,0.09)",
              transform: "rotate(32deg)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              top: 210,
              right: 150,
              width: 120,
              height: 120,
              opacity: 0.25,
              backgroundImage:
                "radial-gradient(#93c5fd 1.4px, transparent 1.4px)",
              backgroundSize: "12px 12px",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              right: 135,
              top: 360,
              width: 420,
              height: 245,
              borderRadius: "999px",
              background:
                "linear-gradient(135deg, rgba(147,197,253,0.96), rgba(59,130,246,0.96))",
              opacity: 0.92,
            }}
          />

          <Box
            sx={{
              position: "relative",
              height: "100%",
              px: 8,
              py: 5.5,
              zIndex: 3,
            }}
          >
            <Box
              component="img"
              src={sintanetLogo}
              alt="Sintanet"
              sx={{
                position: "absolute",
                top: -50,
                left: 15,
                width: 550,
                objectFit: "contain",
                zIndex: 5,
              }}
            />

            <Box sx={{ mt: 22, maxWidth: 520 }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: "#0f172a",
                  fontSize: 46,
                  lineHeight: 1.05,
                  letterSpacing: "-0.06em",
                }}
              />

              <Typography
                sx={{
                  mt: 4,
                  ml: 1,
                  color: "#64748b",
                  fontSize: 20,
                  lineHeight: 1.7,
                  maxWidth: 480,
                }}
              >
                Aceda à sua área pessoal e tenha tudo o que precisa, num só
                lugar.
              </Typography>
            </Box>

            <Box
              component="img"
              src={brunoImg}
              alt="Bruno Rodrigues"
              sx={{
                position: "absolute",
                top: 111,
                right: 105,
                width: 250,
                zIndex: 5,
                objectFit: "contain",
                filter: "drop-shadow(0 30px 45px rgba(15,23,42,0.14))",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: 327,
                right: 115,
                px: 2.2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "#0f172a",
                boxShadow: "0 22px 45px rgba(15,23,42,0.22)",
                zIndex: 6,
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: 950, fontSize: 14 }}>
                BRUNO RODRIGUES
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: "#f97316",
                  fontWeight: 900,
                  fontSize: 11,
                }}
              >
                CEO & FOUNDER
              </Typography>
            </Box>

            <Stack direction="row" spacing={2.4} sx={{ mt: 10 }}>
              <InfoCard
                icon={<LocalMallRoundedIcon />}
                title="Loja de confiança"
                text="Os melhores produtos e marcas do mercado."
                color={{ bg: "#eef2ff", main: "#6366f1" }}
              />
              <InfoCard
                icon={<ShieldOutlinedIcon />}
                title="Apoio especializado"
                text="Assistência dedicada sempre que precisar."
                color={{ bg: "#fff7ed", main: "#f97316" }}
              />
              <InfoCard
                icon={<RocketLaunchRoundedIcon />}
                title="Experiência rápida"
                text="Navegação simples, segura e eficiente."
                color={{ bg: "#ecfeff", main: "#06b6d4" }}
              />
            </Stack>

            <Box
              sx={{
                mt: 6,
                p: 2.2,
                borderRadius: "24px",
                bgcolor: "rgba(255,255,255,0.84)",
                border: "1px solid rgba(226,232,240,0.9)",
                boxShadow: "0 18px 45px rgba(15,23,42,0.06)",
              }}
            >
              <Stack
                direction="row"
                spacing={2.8}
                alignItems="center"
                justifyContent="space-between"
              >
                <ContactItem
                  icon={<PhoneRoundedIcon fontSize="small" />}
                  title="+351 911 111 156"
                  subtitle="Telefone"
                />
                <ContactItem
                  icon={<EmailRoundedIcon fontSize="small" />}
                  title="geral@sintanet.pt"
                  subtitle="E-mail"
                />
                <ContactItem
                  icon={<LanguageRoundedIcon fontSize="small" />}
                  title="www.sintanet.pt"
                  subtitle="Website"
                />
                <ContactItem
                  icon={<LocationOnRoundedIcon fontSize="small" />}
                  title="C. Comercial Passerelle Loja Nº 62"
                  subtitle="4805-121 Caldas das Taipas"
                />
              </Stack>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 5 }}
            >
              <Typography sx={{ fontWeight: 950, fontSize: 32, color: "#111827" }}>
                moloni
              </Typography>
              <Typography sx={{ fontWeight: 950, fontSize: 36, color: "#0ea5e9" }}>
                CHIP7
              </Typography>
              <Typography sx={{ fontWeight: 950, fontSize: 38, color: "#111827" }}>
                WE ARE{" "}
                <Box component="span" sx={{ color: "#f97316" }}>
                  ALL
                </Box>
              </Typography>
            </Stack>

            <Typography
              sx={{
                mt: 2.8,
                color: "#64748b",
                textAlign: "center",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: ".04em",
              }}
            >
              CALDAS DAS TAIPAS · PÓVOA DE LANHOSO · AMARES · ARCO DE BAÚLHE ·
              LOUSADA
            </Typography>

            <Box
              sx={{
                width: 40,
                height: 3,
                bgcolor: "#f97316",
                borderRadius: 999,
                mx: "auto",
                mt: 2.2,
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}